import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { FiUser } from 'react-icons/fi';
import { Clock, User } from 'lucide-react';
import NotificationPopup from '../../component/NotificationPopup';
import api, { expireInvite, analyzeSkills, resendInterviewInviteEmail, getInterviewReportStatus, getCandidateInterviewsDigest } from '../../../../api/client';
import { useCandidates, useJobDescriptions, useSkillAnalysis } from '../../../../hooks/useCandidateDetailApis';
import { PageCard, PageShell } from '../../../common/PageHeader';
import CandidateDetailsHeader from './CandidateDetailsHeader';
import CandidateInfoCards from './CandidateInfoCards';
import CandidateJDTagsSection from './CandidateJDTagsSection';
import CandidateStatistics from './CandidateStatistics';
import LiveInterviewProgress from './LiveInterviewProgress';
import ScheduledInterviews from './ScheduledInterviews';
import InterviewSessions from './InterviewSessions';
import ViewResult from '../../component/ViewResult';
import GenerateInviteModal from '../../../modals/GenerateInviteModal';
import GenerateInviteSuccessfully from '../../component/GenerateInviteSuccessfully';
import DynamicBasicDialog from '../../component/DynamicBasicDialog';
import EmailConfirmModal from './EmailConfirmModal';
import MultiJDScheduleModal from './MultiJDScheduleModal';
import SkillAnalysisModal from './SkillAnalysisModal';
import Timeline from '../../component/Timeline';
import UpdateResumeModal from '../../../modals/UpdateResumeModal';
import { CommentsModal } from '../../../comments';
import FeedbackModal from '../../interviewOverview/FeedbackModal';
import { usePageLoading } from '../../../../contexts/LoadingContext';

const REPORT_POLL_INTERVAL_MS = 5000;
const INTERVIEW_DIGEST_POLL_INTERVAL_MS = 15000;
const INTERVIEW_PAGE_SIZE = 10;

// A completed interview whose report has not landed yet. `report_status` is only
// present on rows the poller has already touched; 'failed'/'not_applicable' mean
// the backend has given up, so we stop polling those.
const isReportPending = (session) => (
    session?.status === 'completed'
    && session?.feedback?.detailed_breakdown?.effective_score == null
    && session?.report_status !== 'failed'
    && session?.report_status !== 'not_applicable'
);

// Signature of the interview list as rendered, compared against the backend digest
// to decide whether a refetch is worth doing at all.
const buildInterviewDigestKey = (count, sessions) => (
    `${count}|${(sessions || []).map(([sessionId, sessionStatus]) => `${sessionId}:${sessionStatus}`).join(',')}`
);

const sameInterviewRow = (a, b) => (
    a.status === b.status
    && a.is_archived === b.is_archived
    && a.started_at === b.started_at
    && a.candidate_has_jd_tag === b.candidate_has_jd_tag
    && a.report_status === b.report_status
    && (a.job_description?.id ?? null) === (b.job_description?.id ?? null)
    && (a.job_description?.title ?? null) === (b.job_description?.title ?? null)
    && (a.feedback?.detailed_breakdown?.effective_score ?? null) === (b.feedback?.detailed_breakdown?.effective_score ?? null)
    && (a.feedback?.detailed_breakdown?.total_score ?? null) === (b.feedback?.detailed_breakdown?.total_score ?? null)
    && JSON.stringify(a.tags || []) === JSON.stringify(b.tags || [])
);

const sameStatistics = (a, b) => (
    !!a && !!b
    && a.total_interviews === b.total_interviews
    && a.completed_interviews === b.completed_interviews
    && a.average_score === b.average_score
    && a.best_score === b.best_score
    && a.last_interview_date === b.last_interview_date
);

// The digest refetch and the report poller race. If a refetch lands before the
// backend has written the Feedback row, it would blank out a score the report
// poller just filled in, so never downgrade a row that already has one.
const preserveGeneratedReport = (prevRow, nextRow) => {
    const prevScore = prevRow?.feedback?.detailed_breakdown?.effective_score;
    const nextScore = nextRow?.feedback?.detailed_breakdown?.effective_score;
    if (prevScore != null && nextScore == null) {
        return { ...nextRow, feedback: prevRow.feedback, report_status: prevRow.report_status };
    }
    return nextRow;
};

// Rebuild the page from the server's rows while keeping the exact object reference
// of every row that did not actually change, so React skips re-rendering them.
const mergeInterviewRows = (prevRows, nextRows) => {
    if (!Array.isArray(prevRows)) return nextRows;

    const prevById = new Map(prevRows.map((row) => [row.id, row]));
    let changed = prevRows.length !== nextRows.length;

    const merged = nextRows.map((nextRow, index) => {
        const prevRow = prevById.get(nextRow.id);
        if (!prevRow) {
            changed = true;
            return nextRow;
        }
        const preserved = preserveGeneratedReport(prevRow, nextRow);
        if (sameInterviewRow(prevRow, preserved)) {
            if (prevRows[index] !== prevRow) changed = true;
            return prevRow;
        }
        changed = true;
        return preserved;
    });

    return changed ? merged : prevRows;
};

export default function CandidateDetails({ onLogout }) {
    const { t, i18n } = useTranslation();
    const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const { id } = useParams();
    const navigate = useNavigate();
    const { generateInvite } = useCandidates();
    const { jobDescriptions } = useJobDescriptions();
    const {
        skillAnalysis,
        setSkillAnalysis,
        createInviteWithSkills,
        reanalyzeSkills
    } = useSkillAnalysis();

    // Candidate data state
    const [candidateData, setCandidateData] = useState(null);
    const [candidateLoading, setCandidateLoading] = useState(true);
    const [candidateError, setCandidateError] = useState('');
    usePageLoading(candidateLoading, { message: t('common.loading', 'Loading…') });

    // Report / interview-list polling state
    const interviewQueryRef = useRef({ page: 1, showArchived: false });
    const previousPendingReportKeyRef = useRef('');
    const renderedDigestKeyRef = useRef('');

    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
    const [expireDialogOpen, setExpireDialogOpen] = useState(false);
    const [expireToken, setExpireToken] = useState(null);

    // Session data
    const [loading, setLoading] = useState(true);
    const [sessionId, setSessionId] = useState('');

    // Feedback modal state
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [loadingFeedback, setLoadingFeedback] = useState(false);
    const [feedbackError, setFeedbackError] = useState('');
    const [sessionData, setSessionData] = useState(null);

    // Invite modal state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);
    const [generatedInvite, setGeneratedInvite] = useState(null);
    const [copiedInviteUrl, setCopiedInviteUrl] = useState(null);

    // Email functionality state
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
    const [emailRecipient, setEmailRecipient] = useState(null);
    const [ccRecipients, setCcRecipients] = useState('');

    // Inline editing state
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Live interview progress state
    const [liveInterviews, setLiveInterviews] = useState([]);
    const [liveInterviewsLoading, setLiveInterviewsLoading] = useState(false);

    // Multi-JD invites state
    const [candidateInvites, setCandidateInvites] = useState([]);
    const [invitesLoading, setInvitesLoading] = useState(true);
    const [invitesError, setInvitesError] = useState('');

    // Scheduled Interviews tab state
    const [scheduledInterviewsTab, setScheduledInterviewsTab] = useState('active');
    const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);

    // Separate state for expired invites
    const [expiredInvites, setExpiredInvites] = useState([]);
    const [expiredInvitesLoading, setExpiredInvitesLoading] = useState(false);
    const [expiredInvitesError, setExpiredInvitesError] = useState('');
    // Multi-JD scheduling state
    const [showMultiJDModal, setShowMultiJDModal] = useState(false);
    const [selectedJDs, setSelectedJDs] = useState([]);
    const [isCreatingMultiJDInvites, setIsCreatingMultiJDInvites] = useState(false);

    // Timeline state
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [multiJDResults, setMultiJDResults] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState({});

    // Skill analysis state
    const [selectedJDForAnalysis, setSelectedJDForAnalysis] = useState(null);
    const [showSkillAnalysisModal, setShowSkillAnalysisModal] = useState(false);
    const [skillAnalysisLoading, setSkillAnalysisLoading] = useState(false);

    // Update Resume State
    const [showUpdateResumeModal, setShowUpdateResumeModal] = useState(false);
    const [isUpdatingResume, setIsUpdatingResume] = useState(false);

    // Comments Modal State
    const [commentsModalOpen, setCommentsModalOpen] = useState(false);
    const [selectedSessionForComments, setSelectedSessionForComments] = useState(null);
    const [selectedSessionData, setSelectedSessionData] = useState(null);

    // Post-Interview Feedback Modal State
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [feedbackModalData, setFeedbackModalData] = useState(null);
    const [feedbackModalLoading, setFeedbackModalLoading] = useState(false);
    const [feedbackModalError, setFeedbackModalError] = useState('');
    const [feedbackCandidateInfo, setFeedbackCandidateInfo] = useState(null);

    const fetchCandidateData = useCallback(async () => {
        if (!id) return;
        try {
            setCandidateLoading(true);
            setCandidateError('');
            const response = await api.get(`candidates/${id}`);
            setCandidateData(response.data);
        } catch (err) {
            console.error('Error fetching candidate data:', err);
            setCandidateError('Failed to load candidate details.');
            if (err.response?.status === 404) {
                setCandidateError('Candidate not found.');
            } else if (err.message === 'Network Error' || !err.response) {
                setCandidateError('Network error. Please check your connection.');
            } else {
                setCandidateError('An error occurred while loading candidate details.');
            }
        } finally {
            setCandidateLoading(false);
        }
    }, [id]);

    const fetchSessionsData = useCallback(async () => {
        try {
            setLoading(true);
            // const response = await api.get(`sessions/`);
        } catch (err) {
            console.error('Error fetching sessions:', err);
            setSnackbar({
                open: true,
                message: 'Failed to load interview sessions.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCandidateInvites = useCallback(async () => {
        if (!id) return;
        try {
            setInvitesLoading(true);
            setInvitesError('');
            const response = await api.get(`admin/candidate/${id}/invites/?status=active&is_used=false`);
            setCandidateInvites(response.data.invites || []);
        } catch (err) {
            console.error('Error fetching candidate invites:', err);
            setInvitesError('Failed to load candidate invites.');
            if (err.response?.status === 404) {
                setInvitesError('Candidate invites not found.');
            } else if (err.message === 'Network Error' || !err.response) {
                setInvitesError('Network error. Please check your connection.');
            }
        } finally {
            setInvitesLoading(false);
        }
    }, [id]);

    const fetchExpiredInvites = useCallback(async () => {
        if (!id) return;
        try {
            setExpiredInvitesLoading(true);
            setExpiredInvitesError('');
            const response = await api.get(`admin/candidate/${id}/invites/?status=expired`);
            setExpiredInvites(response.data.invites || []);
        } catch (err) {
            console.error('Error fetching expired invites:', err);
            setExpiredInvitesError('Failed to load expired invites.');
            if (err.response?.status === 404) {
                setExpiredInvitesError('Expired invites not found.');
            } else if (err.message === 'Network Error' || !err.response) {
                setExpiredInvitesError('Network error. Please check your connection.');
            }
        } finally {
            setExpiredInvitesLoading(false);
        }
    }, [id]);

    const fetchLiveInterviews = useCallback(async () => {
        if (!id) return;
        setLiveInterviewsLoading(true);
        try {
            const response = await api.get(`/candidates/${id}/live_interviews/`);
            setLiveInterviews(response.data.live_interviews || []);
        } catch (error) {
            console.error('Error fetching live interviews:', error);
        } finally {
            setLiveInterviewsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchCandidateData();
            fetchSessionsData();
            fetchLiveInterviews();
            fetchCandidateInvites();
            checkCompanyAdminStatus();
        }
    }, [id, fetchCandidateData, fetchSessionsData, fetchLiveInterviews, fetchCandidateInvites]);

    // Fetch expired invites when expired tab is selected
    useEffect(() => {
        if (id && scheduledInterviewsTab === 'expired') {
            fetchExpiredInvites();
        }
    }, [id, scheduledInterviewsTab, fetchExpiredInvites]);

    // Set up polling for live interview updates
    useEffect(() => {
        if (id && liveInterviews.length > 0) {
            const interval = setInterval(() => {
                fetchLiveInterviews();
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [id, liveInterviews.length, fetchLiveInterviews]);

    const checkCompanyAdminStatus = async () => {
        try {
            const response = await api.get('admin/company-admin-check/', {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            setIsCompanyAdmin(response.data.is_company_admin || false);
        } catch (err) {
            console.error('Error checking company admin status:', err);
            setIsCompanyAdmin(false);
        }
    };

    const handleConfirmInvite = async (candidateId, jobDescriptionId, languageCode = null, scheduleConfig = null) => {
        setIsGeneratingInvite(true);
        try {
            const response = await generateInvite(candidateId, jobDescriptionId, languageCode, scheduleConfig);
            const data = response.data;
            setGeneratedInvite({ ...data, interviewUrl: getUpdatedLink(data.interviewUrl) });

            setSnackbar({
                open: true,
                message: 'Invite generated successfully',
                severity: 'success'
            });
            fetchCandidateData();
            fetchCandidateInvites();
            if (scheduledInterviewsTab === 'expired') {
                fetchExpiredInvites();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.error || 'Failed to generate interview invite. Please try again.';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });

            if (errorMessage.includes('Not enough credits')) {
                setSnackbar({
                    open: true,
                    message: 'Not enough credits to generate invite. Please add more credits to your company account.',
                    severity: 'error',
                    autoHideDuration: 6000
                });
            }
            throw err;
        } finally {
            setIsGeneratingInvite(false);
        }
    };
// Multi-JD scheduling handlers
const handleMultiJDSchedule = () => {
    setSelectedJDs([]);
    setMultiJDResults(null);
    setSelectedSkills({});
    setShowMultiJDModal(true);
};

const handleCreateMultiJDInvites = async (languageCode = null, scheduleConfig = null) => {
    if (!candidateData || selectedJDs.length === 0) {
        setSnackbar({
            open: true,
            message: t('candidateDetails.multiJDSchedule.selectAtLeastOneJd'),
            severity: 'error'
        });
        return;
    }

    setIsCreatingMultiJDInvites(true);

    try {
        const payload = {
            candidateId: candidateData.id,
            jobDescriptionIds: selectedJDs,
            sendEmail: true
        };
        if (languageCode) {
            payload.language = languageCode;
        }
        if (scheduleConfig?.global) {
            payload.max_no_of_questions = scheduleConfig.global.max_no_of_questions;
            payload.interview_duration_minutes = scheduleConfig.global.interview_duration_minutes;
            payload.require_audio_verification = scheduleConfig.global.require_audio_verification;
            payload.max_no_of_questions_by_jd = Object.fromEntries(
                Object.entries(scheduleConfig.overrides || {}).map(([jdId, cfg]) => [jdId, cfg.max_no_of_questions])
            );
            payload.interview_duration_minutes_by_jd = Object.fromEntries(
                Object.entries(scheduleConfig.overrides || {}).map(([jdId, cfg]) => [jdId, cfg.interview_duration_minutes])
            );
        }
        console.log('=== Multi-JD Invite (no skills) ===');
        console.log('Language code:', languageCode);
        console.log('Payload:', payload);
        
        const response = await api.post('/admin/generate-multi-jd-invites/', payload);

        setMultiJDResults(response.data);
        setSnackbar({
            open: true,
            message: t('candidateDetails.multiJDSchedule.createSuccess', {
                count: response.data.totalSuccessful,
                name: response.data.candidateName
            }),
            severity: 'success'
        });

        // Refresh data
        fetchCandidateData();
        fetchCandidateInvites();
        if (scheduledInterviewsTab === 'expired') {
            fetchExpiredInvites();
        }
    } catch (error) {
        console.error('Error creating multi-JD invites:', error);
        setSnackbar({
            open: true,
            message: error.response?.data?.error || t('candidateDetails.multiJDSchedule.createFailed'),
            severity: 'error'
        });
    } finally {
        setIsCreatingMultiJDInvites(false);
    }
};

const handleCreateMultiJDInvitesWithSkills = async (languageCode = null, scheduleConfig = null) => {
    if (!candidateData || selectedJDs.length === 0) {
        setSnackbar({
            open: true,
            message: t('candidateDetails.multiJDSchedule.selectAtLeastOneJd'),
            severity: 'error'
        });
        return;
    }

    setIsCreatingMultiJDInvites(true);

    try {
        const results = [];
        let totalSuccessful = 0;
        let totalFailed = 0;

        console.log('=== Multi-JD Invite Creation ===');
        console.log('Language code received:', languageCode);
        console.log('Selected JDs:', selectedJDs);

        for (const jdId of selectedJDs) {
            try {
                const selectedSkillsForJD = selectedSkills[jdId] || [];
                const response = await createInviteWithSkills(
                    candidateData.id,
                    jdId,
                    selectedSkillsForJD,
                    languageCode,  // Pass language code to each invite
                    scheduleConfig
                        ? {
                            ...(scheduleConfig.global || {}),
                            ...(scheduleConfig.overrides?.[jdId] || {}),
                            require_audio_verification: scheduleConfig.global?.require_audio_verification
                        }
                        : null
                );

                // Add more detailed logging
                console.log(`Success for JD ${jdId}:`, response);

                const jd = jobDescriptions.find(jd => jd.id === jdId);
                results.push({
                    jobDescriptionTitle: jd?.title || t('candidateDetails.multiJDSchedule.jdFallbackTitle', { id: jdId }),
                    interviewUrl: response.data.interviewUrl,
                    status: 'created',
                    success: true // Add explicit success flag
                });
                totalSuccessful++;
            } catch (error) {
                // Add more detailed logging
                console.error(`Error for JD ${jdId}:`, error);
                console.error('Error response:', error.response);
                
                const jd = jobDescriptions.find(jd => jd.id === jdId);
                results.push({
                    jobDescriptionTitle: jd?.title || t('candidateDetails.multiJDSchedule.jdFallbackTitle', { id: jdId }),
                    error: error.response?.data?.error || error.message || t('candidateDetails.multiJDSchedule.inviteCreateFailed'),
                    success: false // Add explicit failure flag
                });
                totalFailed++;
            }
        }

        console.log('Final results:', results);
        console.log('Total successful:', totalSuccessful);
        console.log('Total failed:', totalFailed);

        setMultiJDResults({
            candidateName: `${candidateData.first_name || ''} ${candidateData.last_name || ''}`.trim(),
            totalSuccessful,
            totalFailed,
            successfulInvites: results.filter(r => r.success === true),
            failedInvites: results.filter(r => r.success === false)
        });

        setSnackbar({
            open: true,
            message: t('candidateDetails.multiJDSchedule.createWithSkillsSuccess', {
                count: totalSuccessful,
                name: `${candidateData.first_name || ''} ${candidateData.last_name || ''}`.trim()
            }),
            severity: 'success'
        });

        // Refresh data
        fetchCandidateData();
        fetchCandidateInvites();
        if (scheduledInterviewsTab === 'expired') {
            fetchExpiredInvites();
        }

    } catch (error) {
        console.error('Error creating multi-JD invites with skills:', error);
        setSnackbar({
            open: true,
            message: error.response?.data?.error || t('candidateDetails.multiJDSchedule.createWithSkillsFailed'),
            severity: 'error'
        });
    } finally {
        setIsCreatingMultiJDInvites(false);
    }
};

    // Skill analysis handlers
    const handleSkillAnalysis = async (jobDescription) => {
        setSelectedJDForAnalysis(jobDescription);
        setShowSkillAnalysisModal(true);
        setSkillAnalysisLoading(true);

        try {
            if (!candidateData?.id || !jobDescription?.id) {
                throw new Error('Missing candidate or job description');
            }
            const data = await analyzeSkills(candidateData.id, jobDescription.id);
            setSkillAnalysis(data);
            setSnackbar({
                open: true,
                message: t('candidateDetails.inviteSchedule.skillAnalysisSuccess'),
                severity: 'success'
            });
        } catch (error) {
            console.error('Error analyzing skills:', error);
            setSnackbar({
                open: true,
                message: error.message || t('candidateDetails.inviteSchedule.skillAnalysisFailed'),
                severity: 'error'
            });
            setShowSkillAnalysisModal(false);
            setSelectedJDForAnalysis(null);
        } finally {
            setSkillAnalysisLoading(false);
        }
    };

    const handleClearCachesAndRestart = async () => {
        if (!candidateData?.id || !selectedJDForAnalysis?.id) return;
        setSkillAnalysisLoading(true);
        try {
            const data = await analyzeSkills(
                candidateData.id,
                selectedJDForAnalysis.id
            );
            setSkillAnalysis(data);
            setSnackbar({
                open: true,
                message: t('candidateDetails.inviteSchedule.skillAnalysisCacheSuccess'),
                severity: 'success',
            });
        } catch (error) {
            console.error('Error re-analyzing after cache clear:', error);
            setSnackbar({
                open: true,
                message:
                    error.message ||
                    t('candidateDetails.inviteSchedule.skillAnalysisCacheFailed'),
                severity: 'error',
            });
        } finally {
            setSkillAnalysisLoading(false);
        }
    };

    const handleApplySkillsAndReturn = () => {
        setShowSkillAnalysisModal(false);
        setSelectedJDForAnalysis(null);
        setSkillAnalysis(null);
    };
    const handleGenerateInviteWithSkills = async (result, candidateId) => {
        setGeneratedInvite(result.data);
        fetchCandidateData();
        fetchCandidateInvites();
        if (scheduledInterviewsTab === 'expired') {
            fetchExpiredInvites();
        }
    };

    const handleUpdateResume = async (newResumeFileOrFormData, fallbackFile) => {
        setIsUpdatingResume(true);
        try {
            let companyId = candidateData?.company?.[0]?.id;
            if (!companyId) {
                try {
                    const adminToken = localStorage.getItem('token');
                    if (adminToken) {
                        const tokenPayload = JSON.parse(atob(adminToken.split('.')[1]));
                        companyId = tokenPayload.company_id;
                    }
                } catch (err) {
                    console.warn('Could not decode token for company_id');
                }
            }

            if (!companyId) {
                setSnackbar({ open: true, message: 'Company ID not found. Associate candidate with a company first.', severity: 'error' });
                setIsUpdatingResume(false);
                return;
            }

            // Accept either a FormData (from modal) or a File (fallback)
            let formData;
            if (newResumeFileOrFormData instanceof FormData) {
                formData = newResumeFileOrFormData;
                // ensure company_id is present
                if (!formData.get('company_id')) formData.append('company_id', companyId);
            } else {
                const newFile = newResumeFileOrFormData || fallbackFile;
                formData = new FormData();
                formData.append('company_id', companyId);
                formData.append('resume', newFile);
            }

            const adminToken = localStorage.getItem('token');

            // Let axios set the multipart Content-Type (including boundary). Only send Authorization if available.
            const headers = {};
            if (adminToken) headers.Authorization = `Token ${adminToken}`;

            await api.patch(`candidates/${id}/`, formData, { headers });

            setSnackbar({
                open: true,
                message: 'Resume updated successfully!',
                severity: 'success'
            });
            setShowUpdateResumeModal(false);
            fetchCandidateData(); // Refresh candidate data to show updated resume info
        } catch (error) {
            console.error('Error updating resume:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to update resume. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsUpdatingResume(false);
        }
    };

    const handleConfirmExpireInvite = async () => {
        try {
            await expireInvite(expireToken);
            setSnackbar({
                open: true,
                message: 'Invite expired successfully',
                severity: 'success'
            });
            fetchCandidateData();
            fetchCandidateInvites();
            if (scheduledInterviewsTab === 'expired') {
                fetchExpiredInvites();
            }
        } catch (err) {
            console.error('Error expiring invite:', err);
            setSnackbar({
                open: true,
                message: 'Failed to expire invite',
                severity: 'error'
            });
        } finally {
            setExpireDialogOpen(false);
            setExpireToken(null);
        }
    };

    const handleResendEmail = async (inviteId) => {
        try {
            await resendInterviewInviteEmail(inviteId);
            setSnackbar({
                open: true,
                message: 'Interview invite email sent successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error resending email:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.error || 'Failed to resend email. Please try again.',
                severity: 'error'
            });
        }
    };

    const getUpdatedLink = (link) => {
        if (!link) return '';
        const url = new URL(link);
        const frontendUrl = (process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000').replace(/\/$/, '');
        return frontendUrl + url.pathname;
    };

    const copyToClipboard = (inviteUrl) => {
        navigator.clipboard.writeText(inviteUrl);
        setCopiedInviteUrl(inviteUrl);
        setTimeout(() => setCopiedInviteUrl(null), 2000);
    };

    const handleDeleteCandidate = async () => {
        try {
            await api.delete(`admin/delete-candidate/${candidateData?.id}/`);
            setDialogOpen(false);
            setSnackbar({
                open: true,
                message: 'Candidate archived successfully.',
                severity: 'success'
            });
            navigate(-1);
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to archive candidate.',
                severity: 'error'
            });
            console.error('Failed to archive candidate:', err);
        }
    };

    const handleDeleteInterview = async () => {
        try {
            await api.delete(`sessions/${sessionId}/`);
            setSnackbar({
                open: true,
                message: 'Interview archived successfully.',
                severity: 'success'
            });
            setSessionDialogOpen(false);
            await fetchCandidateData();
            await fetchSessionsData();
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to archive interview.',
                severity: 'error'
            });
            console.error('Failed to archive interview:', err);
        }
    };

    const handleViewResults = async (sessionId) => {
        if (!sessionId) return;
        setShowFeedbackModal(true);
        setLoadingFeedback(true);
        setFeedbackError('');
        setFeedback(null);

        try {
            const sessionRes = await api.get(`sessions/${sessionId}/`);
            setSessionData(sessionRes.data);

            const res = await api.get(`feedback/?session=${sessionId}`);
            if (!res.data || !res.data.results || res.data.results.length === 0) {
                setFeedbackError('No feedback found for this session.');
                return;
            }
            const feedbackData = res.data.results[0];
            setFeedback(feedbackData);

        } catch (err) {
            setFeedbackError('Failed to load feedback.');
            console.error('Error loading feedback:', err);
        } finally {
            setLoadingFeedback(false);
        }
    };

    const handleViewFeedback = async (sessionId, sessionInfo) => {
        if (!sessionId) return;
        setFeedbackCandidateInfo(sessionInfo);
        setFeedbackModalOpen(true);
        setFeedbackModalLoading(true);
        setFeedbackModalError('');
        setFeedbackModalData(null);
        try {
            const res = await api.get(`feedback-questionnaires/responses/${sessionId}/`);
            setFeedbackModalData(res.data || null);
        } catch (err) {
            console.error('Failed to fetch questionnaire feedback:', err);
            setFeedbackModalError(
                t('feedback.fetchError', 'Failed to load feedback. Please try again.')
            );
        } finally {
            setFeedbackModalLoading(false);
        }
    };

    const sendEmail = async (feedback, sessionId, candidateEmail, candidateName, htmlContent = null, filename = null) => {
        setEmailRecipient({
            name: candidateName,
            email: candidateEmail,
            htmlContent: htmlContent,
            filename: filename
        });
        setShowEmailConfirmModal(true);
        setShowFeedbackModal(false);
    };

    const handleConfirmEmail = async () => {
        if (!emailRecipient) return;

        setIsSendingEmail(true);
        try {
            const emailData = {
                sessionId: feedback?.session,
                candidateEmail: emailRecipient.email,
                candidateName: emailRecipient.name,
                ccRecipients: ccRecipients.split(',').map(email => email.trim()).filter(email => email),
                html_content: emailRecipient.htmlContent,
                filename: emailRecipient.filename,
                feedback: feedback
            };

            await api.post('admin/send-feedback-email/', emailData, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('adminToken')}`,
                },
            });

            setEmailSent(true);
            setSnackbar({
                open: true,
                message: 'Feedback email sent successfully!',
                severity: 'success'
            });

            setTimeout(() => {
                setShowEmailConfirmModal(false);
                setShowFeedbackModal(false);
                setEmailSent(false);
                setCcRecipients('');
                setEmailRecipient(null);
            }, 2000);

        } catch (error) {
            console.error('Error sending email:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to send feedback email',
                severity: 'error'
            });
        } finally {
            setIsSendingEmail(false);
        }
    };

    // Add this function with your other handler functions (around line 400-500)
    const fetchInterviewPage = async (pageNumber, showArchived = false) => {
        if (!id) return;

        interviewQueryRef.current = { page: pageNumber, showArchived };

        try {
            setLoading(true);
            const showArchivedParam = showArchived ? '&show_archived=true' : '';
            const response = await api.get(`candidates/${id}/?interview_page=${pageNumber}&interview_page_size=${INTERVIEW_PAGE_SIZE}${showArchivedParam}`);

            // Update only the interview-related data
            setCandidateData(prev => ({
                ...prev,
                interviews: response.data.interviews,
                statistics: response.data.statistics || prev.statistics
            }));
        } catch (err) {
            console.error('Error fetching interview page:', err);
            setSnackbar({
                open: true,
                message: 'Failed to load interview page.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Merge freshly generated report scores into the interview rows in place.
    // Deliberately never touches `loading` and never refetches the interview list,
    // so the table is not swapped for a spinner while a report lands. Rows whose
    // status is unchanged keep their exact object reference.
    const pollInterviewReports = useCallback(async (sessionIds) => {
        const updates = await Promise.all(sessionIds.map(async (sessionId) => {
            try {
                return await getInterviewReportStatus(sessionId);
            } catch (err) {
                console.error(`Error polling report status for interview ${sessionId}:`, err);
                return null;
            }
        }));

        const byId = new Map(
            updates.filter((update) => update && update.id != null).map((update) => [update.id, update])
        );
        if (byId.size === 0) return;

        setCandidateData((prev) => {
            const rows = prev?.interviews?.results;
            if (!Array.isArray(rows)) return prev;

            let changed = false;
            const nextRows = rows.map((row) => {
                const update = byId.get(row.id);
                if (!update) return row;

                const breakdown = row?.feedback?.detailed_breakdown;
                const nextEffectiveScore = update.effective_score ?? null;
                const nextTotalScore = update.total_score ?? null;
                if (
                    row.report_status === update.report_status
                    && (breakdown?.effective_score ?? null) === nextEffectiveScore
                    && (breakdown?.total_score ?? null) === nextTotalScore
                ) {
                    return row;
                }

                changed = true;
                const nextRow = { ...row, report_status: update.report_status };
                if (update.report_status === 'ready') {
                    nextRow.feedback = {
                        ...(row.feedback || {}),
                        id: update.feedback_id,
                        detailed_breakdown: {
                            ...(breakdown || {}),
                            effective_score: nextEffectiveScore,
                            total_score: nextTotalScore,
                        },
                    };
                }
                return nextRow;
            });

            return changed
                ? { ...prev, interviews: { ...prev.interviews, results: nextRows } }
                : prev;
        });
    }, []);

    // Statistics are computed server-side across all sessions, so they stay stale
    // while reports generate. Refresh them once, silently, after the last pending
    // report resolves. `interview_page_size=1` keeps the payload minimal.
    const refreshInterviewStatistics = useCallback(async () => {
        if (!id) return;
        try {
            const showArchivedParam = interviewQueryRef.current.showArchived ? '&show_archived=true' : '';
            const response = await api.get(`candidates/${id}/?interview_page=1&interview_page_size=1${showArchivedParam}`);
            const statistics = response.data?.statistics;
            if (!statistics) return;
            setCandidateData((prev) => (prev ? { ...prev, statistics } : prev));
        } catch (err) {
            console.error('Error refreshing interview statistics:', err);
        }
    }, [id]);

    // Stable, sorted key of the interviews still waiting on a report. Using a
    // string means the interval is only rebuilt when the pending set actually
    // changes, and polling stops entirely once the key is empty.
    const pendingReportKey = useMemo(() => (
        (candidateData?.interviews?.results || [])
            .filter(isReportPending)
            .map((session) => session.id)
            .sort((a, b) => a - b)
            .join(',')
    ), [candidateData]);

    useEffect(() => {
        if (!pendingReportKey) return undefined;
        const sessionIds = pendingReportKey.split(',').map(Number);

        pollInterviewReports(sessionIds);
        const interval = setInterval(() => {
            pollInterviewReports(sessionIds);
        }, REPORT_POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [pendingReportKey, pollInterviewReports]);

    useEffect(() => {
        const hadPendingReports = previousPendingReportKeyRef.current !== '';
        previousPendingReportKeyRef.current = pendingReportKey;
        if (hadPendingReports && !pendingReportKey) {
            refreshInterviewStatistics();
        }
    }, [pendingReportKey, refreshInterviewStatistics]);

    // Refetch the current interview page and merge it in place. Unlike
    // fetchInterviewPage this never sets `loading`, so the table is not replaced by
    // a spinner when an interview appears or changes state.
    const refreshInterviewsSilently = useCallback(async () => {
        if (!id) return;
        const { page, showArchived } = interviewQueryRef.current;

        try {
            const showArchivedParam = showArchived ? '&show_archived=true' : '';
            const response = await api.get(`candidates/${id}/?interview_page=${page}&interview_page_size=${INTERVIEW_PAGE_SIZE}${showArchivedParam}`);
            const interviews = response.data?.interviews;
            if (!interviews) return;

            setCandidateData((prev) => {
                if (!prev) return prev;

                const previousRows = prev.interviews?.results;
                const mergedRows = mergeInterviewRows(previousRows, interviews.results || []);
                const statistics = sameStatistics(prev.statistics, response.data?.statistics)
                    ? prev.statistics
                    : (response.data?.statistics || prev.statistics);

                const rowsChanged = mergedRows !== previousRows;
                const metaChanged = prev.interviews?.count !== interviews.count
                    || prev.interviews?.num_pages !== interviews.num_pages
                    || prev.interviews?.current_page !== interviews.current_page;
                const statisticsChanged = statistics !== prev.statistics;

                if (!rowsChanged && !metaChanged && !statisticsChanged) return prev;

                return {
                    ...prev,
                    interviews: { ...interviews, results: mergedRows },
                    statistics,
                };
            });
        } catch (err) {
            console.error('Error refreshing interview list:', err);
        }
    }, [id]);

    // Signature of what is currently rendered. Held in a ref as well so the poll
    // callback stays stable and does not rebuild the interval on every data change.
    const renderedDigestKey = useMemo(() => {
        const interviews = candidateData?.interviews;
        if (!interviews) return '';
        const rows = interviews.results || [];
        return buildInterviewDigestKey(
            interviews.count ?? rows.length,
            rows.map((session) => [session.id, session.status])
        );
    }, [candidateData]);

    useEffect(() => {
        renderedDigestKeyRef.current = renderedDigestKey;
    }, [renderedDigestKey]);

    // Ask the backend only for (id, status) pairs of the current page. The full
    // detail payload is refetched solely when that signature differs.
    const pollInterviewsDigest = useCallback(async () => {
        if (!id || !renderedDigestKeyRef.current) return;
        const { page, showArchived } = interviewQueryRef.current;

        try {
            const digest = await getCandidateInterviewsDigest(id, {
                page,
                pageSize: INTERVIEW_PAGE_SIZE,
                showArchived,
            });
            const digestKey = buildInterviewDigestKey(digest.count, digest.sessions);
            if (digestKey === renderedDigestKeyRef.current) return;

            await refreshInterviewsSilently();
        } catch (err) {
            console.error('Error polling interview list digest:', err);
        }
    }, [id, refreshInterviewsSilently]);

    // This poll has no natural stop condition, so pause it while the tab is hidden
    // and poll once immediately on return.
    useEffect(() => {
        if (!id) return undefined;

        let interval = null;

        const stopPolling = () => {
            if (interval) {
                clearInterval(interval);
                interval = null;
            }
        };

        const startPolling = () => {
            if (interval) return;
            pollInterviewsDigest();
            interval = setInterval(pollInterviewsDigest, INTERVIEW_DIGEST_POLL_INTERVAL_MS);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                startPolling();
            } else {
                stopPolling();
            }
        };

        handleVisibilityChange();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopPolling();
        };
    }, [id, pollInterviewsDigest]);

    // const hasAnyInvites = () => {
    //     const hasActiveInvites = candidateInvites && candidateInvites.length > 0;
    //     const hasExpiredInvites = expiredInvites && expiredInvites.length > 0;
    //     return hasActiveInvites || hasExpiredInvites;
    // };
    if (candidateLoading) return null;

    if (candidateError) {
        return (
            <PageShell fill={false} className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
                <div className="w-full px-4 py-6">
                    <div className="text-center py-16">
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8 max-w-md mx-auto">
                            <FiUser size={48} className="mx-auto mb-4 text-gray-400" />
                            <Typography variant="h6" color="textSecondary">
                                {candidateError}
                            </Typography>
                        </div>
                    </div>
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell fill={false} className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            <PageCard overflow={false}>
                    <CandidateDetailsHeader
                        candidateData={candidateData}
                        navigate={navigate}
                        onGenerateInvite={() => setShowInviteModal(true)}
                        onExpireInvite={(token) => {
                            setExpireToken(token);
                            setExpireDialogOpen(true);
                        }}
                        onDeleteCandidate={() => setDialogOpen(true)}
                        onMultiJDSchedule={handleMultiJDSchedule}
                        onOpenTimeline={() => setTimelineOpen(true)}
                        resumeUrl={candidateData?.resume_url}
                    />

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 p-3 sm:p-4">
                    {/* Left Column - Candidate Info & Stats */}
                    <div className="xl:col-span-1 space-y-6">
                        {/* Candidate Info Card */}
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                                    <User className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">{t('candidateDetails.title')}</h3>
                            </div>
                            <CandidateInfoCards
                                candidateData={candidateData}
                                setCandidateData={setCandidateData}
                                editingField={editingField}
                                setEditingField={setEditingField}
                                editValue={editValue}
                                setEditValue={setEditValue}
                                isSaving={isSaving}
                                setIsSaving={setIsSaving}
                                setSnackbar={setSnackbar}
                                id={id}
                                onUpdateResume={() => setShowUpdateResumeModal(true)}
                            />
                        </div>

                        {/* JD Tags Card */}
                        <div className="relative z-10">
                            <CandidateJDTagsSection
                                candidateData={candidateData}
                                setCandidateData={setCandidateData}
                                candidateId={id}
                                jobDescriptions={jobDescriptions}
                                setSnackbar={setSnackbar}
                            />
                        </div>

                        {/* Statistics Card */}
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
                            <CandidateStatistics candidateData={candidateData} />
                        </div>

                        {/* Live Interviews Card */}
                        {liveInterviews.length > 0 && (
                            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
                                <LiveInterviewProgress
                                    liveInterviews={liveInterviews}
                                    liveInterviewsLoading={liveInterviewsLoading}
                                    navigate={navigate}
                                    onInterviewEnded={fetchLiveInterviews}
                                />
                            </div>
                        )}
                    </div>

                    {/* Right Column - Interviews & Sessions */}
                    <div className="xl:col-span-2 space-y-6">
                        {/* Scheduled Interviews Card */}
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
                            <ScheduledInterviews
                                candidateInvites={candidateInvites}
                                expiredInvites={expiredInvites}
                                invitesLoading={invitesLoading}
                                expiredInvitesLoading={expiredInvitesLoading}
                                invitesError={invitesError}
                                expiredInvitesError={expiredInvitesError}
                                scheduledInterviewsTab={scheduledInterviewsTab}
                                setScheduledInterviewsTab={setScheduledInterviewsTab}
                                onExpireInvite={(token) => {
                                    setExpireToken(token);
                                    setExpireDialogOpen(true);
                                }}
                                onResendEmail={handleResendEmail}
                                onViewResults={(sessionId) => handleViewResults(sessionId)}
                                setSnackbar={setSnackbar}
                                onMultiJDSchedule={handleMultiJDSchedule}
                                onRefresh={() => {
                                    if (scheduledInterviewsTab === 'active') {
                                        fetchCandidateInvites();
                                    } else {
                                        fetchExpiredInvites();
                                    }
                                }}
                            />
                        </div>

                        {/* Interview Sessions Card */}
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
                            <InterviewSessions
                                candidateData={candidateData}
                                setCandidateData={setCandidateData}
                                loading={loading}
                                isCompanyAdmin={isCompanyAdmin}
                                navigate={navigate}
                                onViewResults={(sessionId) => handleViewResults(sessionId)}
                                fetchPage={fetchInterviewPage}
                                onDeleteSession={(sessionId) => {
                                    setSessionId(sessionId);
                                    setSessionDialogOpen(true);
                                }}
                                candidateId={id}
                                onOpenComments={(sessionId, sessionData) => {
                                    setSelectedSessionForComments(sessionId);
                                    setSelectedSessionData(sessionData);
                                    setCommentsModalOpen(true);
                                }}
                                onViewFeedback={handleViewFeedback}
                            />
                        </div>
                    </div>
                </div>
            </PageCard>

            {/* Modals and Dialogs */}
            {/* Multi-JD Scheduling Modal */}
            <MultiJDScheduleModal
                isOpen={showMultiJDModal}
                onClose={() => {
                    setShowMultiJDModal(false);
                    setSelectedSkills({});
                    setSelectedJDs([]);
                    setMultiJDResults(null);
                }}
                selectedCandidate={candidateData}
                jobDescriptions={jobDescriptions}
                onCreateInvites={handleCreateMultiJDInvites}
                onCreateInvitesWithSkills={handleCreateMultiJDInvitesWithSkills}
                onSkillAnalysis={handleSkillAnalysis}
                selectedJDs={selectedJDs}
                setSelectedJDs={setSelectedJDs}
                selectedSkills={selectedSkills}
                setSelectedSkills={setSelectedSkills}
                multiJDResults={multiJDResults}
                isCreatingInvites={isCreatingMultiJDInvites}
                setSnackbar={setSnackbar}
            />

            {/* Skill Analysis Modal */}
            {showSkillAnalysisModal && (
                <SkillAnalysisModal
                    isOpen={showSkillAnalysisModal}
                    onClose={handleApplySkillsAndReturn}
                    selectedCandidate={candidateData}
                    selectedJDForAnalysis={selectedJDForAnalysis}
                    skillAnalysis={skillAnalysis}
                    skillAnalysisLoading={skillAnalysisLoading}
                    selectedSkills={selectedSkills}
                    setSelectedSkills={setSelectedSkills}
                    onApplySkillsAndReturn={handleApplySkillsAndReturn}
                    reanalyzeSkills={reanalyzeSkills}
                    onClearCachesAndRestart={handleClearCachesAndRestart}
                />
            )}
            {showFeedbackModal && (
                <ViewResult
                    feedback={feedback}
                    loadingFeedback={loadingFeedback}
                    feedbackError={feedbackError}
                    onClose={() => setShowFeedbackModal(false)}
                    sessionData={{
                        ...sessionData,
                        aadhaar_card: sessionData?.aadhaar_card || candidateData?.profile?.aadhaar_card,
                        current_photo: sessionData?.current_photo || candidateData?.profile?.current_photo
                    }}
                    isSendingEmail={isSendingEmail}
                    emailSent={emailSent}
                    sendEmail={sendEmail}
                    callFrom="candidateDetails"
                />
            )}

            {showEmailConfirmModal && (
                <EmailConfirmModal
                    emailRecipient={emailRecipient}
                    setEmailRecipient={setEmailRecipient}
                    ccRecipients={ccRecipients}
                    setCcRecipients={setCcRecipients}
                    isSendingEmail={isSendingEmail}
                    onClose={() => {
                        setShowEmailConfirmModal(false);
                        setCcRecipients('');
                    }}
                    onConfirm={handleConfirmEmail}
                />
            )}

            <NotificationPopup
                open={snackbar.open}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />

            {dialogOpen && (
                <DynamicBasicDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onConfirm={handleDeleteCandidate}
                    title={t('candidateDetails.archiveConfirmation')}
                    content={t('candidateDetails.archiveCandidateMessage')}
                />
            )}

            {sessionDialogOpen && (
                <DynamicBasicDialog
                    open={sessionDialogOpen}
                    onClose={() => setSessionDialogOpen(false)}
                    onConfirm={handleDeleteInterview}
                    title={t('candidateDetails.archiveConfirmation')}
                    content={t('candidateDetails.archiveSessionMessage')}
                />
            )}

            {expireDialogOpen && (
                <DynamicBasicDialog
                    open={expireDialogOpen}
                    onClose={() => {
                        setExpireDialogOpen(false);
                        setExpireToken(null);
                    }}
                    onConfirm={handleConfirmExpireInvite}
                    title={t('candidateDetails.expireInviteConfirmation')}
                    content={t('candidateDetails.expireInviteMessage')}
                />
            )}

            <GenerateInviteModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                selectedCandidate={candidateData}
                onGenerateInvite={handleConfirmInvite}
                onGenerateInviteWithSkills={handleGenerateInviteWithSkills}
                isGeneratingInvite={isGeneratingInvite}
                setSnackbar={setSnackbar}
            />

            {generatedInvite && (
                <GenerateInviteSuccessfully
                    open={!!generatedInvite}
                    onClose={() => setGeneratedInvite(null)}
                    generatedInvite={generatedInvite}
                    copiedInviteUrl={copiedInviteUrl}
                    copyToClipboard={copyToClipboard}
                />
            )}
            
            <UpdateResumeModal
                isOpen={showUpdateResumeModal}
                onClose={() => setShowUpdateResumeModal(false)}
                onConfirm={handleUpdateResume}
                isUpdating={isUpdatingResume}
                setSnackbar={setSnackbar} // Pass setSnackbar prop
            />

            {/* Timeline Drawer */}
            <Timeline
                open={timelineOpen}
                onClose={() => setTimelineOpen(false)}
                apiEndpoint={`audit/timeline/candidatedetails/${id}/`}
                title={t('candidateDetails.candidateTimeline')}
                pageName={t('candidateDetails.title')}
                width={450}
            />

            {/* Comments Modal - Rendered at root level */}
            {selectedSessionForComments && (
                <CommentsModal
                    isOpen={commentsModalOpen}
                    onClose={() => {
                        setCommentsModalOpen(false);
                        setSelectedSessionForComments(null);
                        setSelectedSessionData(null);
                    }}
                    module="session"
                    objectId={selectedSessionForComments}
                    moduleName={t('candidateDetails.interviewSessions.title', 'Interview Session')}
                    objectName={selectedSessionData 
                        ? `${t('candidateDetails.interviewSessions.interviewId', { id: selectedSessionForComments })}${selectedSessionData?.job_description?.title ? ` - ${selectedSessionData.job_description.title}` : ''}`
                        : `${t('candidateDetails.interviewSessions.interviewId', { id: selectedSessionForComments })}`
                    }
                    objectDetails={selectedSessionData}
                />
            )}

            {/* Enhanced Timeline Button */}
            <button
                onClick={() => setTimelineOpen(true)}
                className={`fixed ${isRTL ? 'left-6' : 'right-6'} top-1/2 -translate-y-1/2 z-40 flex items-center justify-center text-white p-3 rounded-2xl transition-all duration-300 group hover:scale-110 hover:shadow-2xl`}
                style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                }}
            >
                <Clock size={16} />
                <span className={`absolute ${isRTL ? 'left-full ml-4' : 'right-full mr-4'} px-3 py-2 bg-gray-900/90 backdrop-blur-sm text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg`}>
                    {t('candidateDetails.timeline')}
                </span>
            </button>

            {/* Post-Interview Feedback Modal */}
            <FeedbackModal
                isOpen={feedbackModalOpen}
                onClose={() => {
                    setFeedbackModalOpen(false);
                    setFeedbackModalData(null);
                    setFeedbackCandidateInfo(null);
                    setFeedbackModalError('');
                }}
                feedbackData={feedbackModalData}
                loading={feedbackModalLoading}
                error={feedbackModalError}
                candidateInfo={feedbackCandidateInfo}
            />
        </PageShell>
    );
}